import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { I18n } from 'react-redux-i18n';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import WarningBilling from './WarningBilling';
import { TAB_LIST } from '../../../BillingUsage';
import { FLEET_BILLING_MODE } from '../../../utils/constants';
import NetWorkPaymentMethod from './netWorkPaymentMethod/NetWorkPaymentMethod';
import { connect } from 'react-redux';
import WithdrawalBilling from './withdrawalBilling';
import { getPayoutFleetMethods } from './withdrawalBilling/withdrawalBillingApi';
import { formatDateBilling, formatDateBillingNotYear } from './CreditGrants/CreditGrants';

const WalletOverview = ({
  billingUsage,
  formatCurrency,
  setIsShowTopupPopup,
  setIsShowAutoRechargePopup,
  viewTransactionIcon,
  viewBankinfoIcon,
  viewSetupIcon,
  permissions,
  onTabSelect,
  auth,
  commonData,
  notification,
  loadingActions,
  onLoadBillingUsage,
  gsnCreditBalance
}) => {
  const isAutoRechargeOn = billingUsage?.topUpPolicy?.autoCharge || false;
  const [isShowNetWorkPaymentMethod, setIsShowNetWorkPaymentMethod] =
    useState(false);
  const [isShowWithdrawal, setIsShowWithdrawal] = useState(false);
  const isInvoiceBillingMode =
    billingUsage?.billingMode === FLEET_BILLING_MODE.invoice;
  const handleWithdrawalClick = async () => {
    const list = await getPayoutFleetMethods({
      fleetId: auth?.selectedFleet?.fleetId,
    });
    if (list.length > 0) {
      setIsShowWithdrawal(true);
    } else {
      setIsShowNetWorkPaymentMethod(true);
    }
  };

  const renderPayoutBalance = () => {
    return (
      <Card className="balance-card cash-balance">
        <Card.Body>
          <div className="card-content">
            <div className="d-flex align-items-center justify-content-between">
              <h4>{I18n.t('billingUsage.payoutBalance')}</h4>
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip">
                    {I18n.t(
                      `billingUsage.${isInvoiceBillingMode ? 'invoicePayoutBalanceTooltip' : 'autoPayoutBalanceTooltip'}`,
                    )}
                  </Tooltip>
                }
                placement="top"
              >
                <IoIosInformationCircleOutline fontSize={18} color="#98A2B3" />
              </OverlayTrigger>
            </div>
            <div className="balance-amount">
              {formatCurrency(billingUsage?.cashBalance || 0)}
            </div>
            {permissions.actions && (
              <Button
                className="btn-header text-add-header p-0"
                onClick={() => handleWithdrawalClick()}
              >
                {I18n.t('billingUsage.withdraw')}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderCreditBalance = () => {
    return (
      <Card className="balance-card credit-balance">
        <Card.Body>
          <div className="card-content">
            <div className="d-flex align-items-center justify-content-between">
              <h4>
                {I18n.t(
                  `billingUsage.${isInvoiceBillingMode ? 'gsnCreditBalance' : 'creditBalance'}`,
                )}
              </h4>
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip">
                    {I18n.t(
                      `billingUsage.${isInvoiceBillingMode ? 'gsnCreditBalanceTooltip' : 'creditBalanceTooltip'}`,
                    )}
                  </Tooltip>
                }
                placement="top"
              >
                <IoIosInformationCircleOutline fontSize={18} color="#98A2B3" />
              </OverlayTrigger>
            </div>
            <div className="balance-amount">
              {formatCurrency( isInvoiceBillingMode ? gsnCreditBalance : billingUsage?.creditBalance || 0)}
            </div>
            {permissions.actions && (
              <Button
                className="btn-header text-add-header p-0"
                onClick={() => setIsShowTopupPopup(true)}
              >
                {I18n.t('billingUsage.topUp')}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderInvoiceSpendingLimit = () => {
    const limit = Number(billingUsage?.spendingLimit) || 0;
    const used = gsnCreditBalance || 0;
    const available = Math.max(0, limit - used);
    const utilizationPct =
      limit > 0 ? Math.min(100, Math.max(0, (used / limit) * 100)) : 0;
    const nextBillingDate = billingUsage?.currentPeriodEnd
      ? formatDateBillingNotYear(billingUsage.currentPeriodEnd)
      : null;

    return (
      <div className="balance-cards balance-cards--credit-line">
        <div className="credit-line-card">
          <div className="credit-line-card__header">
            <span className="credit-line-card__title">
              {I18n.t('billingUsage.creditLineTitle')}
            </span>
            <OverlayTrigger
              overlay={
                <Tooltip id="credit-line-tooltip">
                  {I18n.t('billingUsage.creditLineTooltip')}
                </Tooltip>
              }
              placement="top"
            >
              <span className="credit-line-card__info">
                <IoIosInformationCircleOutline
                  fontSize={18}
                  color="#98A2B3"
                />
              </span>
            </OverlayTrigger>
          </div>
          <div className="credit-line-card__stats">
            <div className="credit-line-card__stat">
              <div className="credit-line-card__stat-label">
                {I18n.t('billingUsage.creditLineLimit')}
              </div>
              <div className="credit-line-card__stat-value">
                {formatCurrency(limit)}
              </div>
            </div>
            <div className="credit-line-card__stat">
              <div className="credit-line-card__stat-label">
                {I18n.t('billingUsage.creditLineUsed')}
              </div>
              <div className="credit-line-card__stat-value">
                {formatCurrency(used)}
              </div>
            </div>
            <div className="credit-line-card__stat">
              <div className="credit-line-card__stat-label">
                {I18n.t('billingUsage.creditLineAvailable')}
              </div>
              <div className="credit-line-card__stat-value">
                {formatCurrency(available)}
              </div>
            </div>
          </div>
          <p className="credit-line-card__next-payment">
            {nextBillingDate ? (
              <>
                <span className="credit-line-card__next-payment-lead">
                  <b>
                    {I18n.t('billingUsage.creditLineNextPaymentLead', {
                      date: nextBillingDate,
                    })}
                  </b>
                </span>{' '}
                <span className="credit-line-card__next-payment-tail">
                  {I18n.t('billingUsage.creditLineNextPaymentTail', {
                    limitAmount: formatCurrency(limit),
                  })}
                </span>
              </>
            ) : (
              <span className="credit-line-card__next-payment-tail">
                {I18n.t('billingUsage.creditLineNextPaymentTailOnly', {
                  limitAmount: formatCurrency(limit),
                })}
              </span>
            )}
          </p>
          <div className="credit-line-card__progress-track">
            <div
              className="credit-line-card__progress-fill"
              style={{ width: `${utilizationPct}%` }}
            />
          </div>
          {permissions.actions && used > 0 && (
            <Button
              className="credit-line-card__make-payment"
              variant="link"
              onClick={() => setIsShowTopupPopup(true)}
            >
              {I18n.t('billingUsage.creditLineMakePayment')}
            </Button>
          )}
        </div>
        <div className="balance-cards">
          {renderPayoutBalance()}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="wallet-overview-section">
        <h3>{isInvoiceBillingMode ? I18n.t('billingUsage.GSNTitle') : I18n.t('billingUsage.walletOverview')}</h3>
        <p className="wallet-subtitle">
          {I18n.t( isInvoiceBillingMode ? 'billingUsage.GSNNote' : 'billingUsage.calculateTotal')}
        </p>
        {isInvoiceBillingMode && billingUsage?.spendingLimit > 0 ? (
          renderInvoiceSpendingLimit()
        ) : (
          <div>
            <div className="balance-cards">
              {renderCreditBalance()}
              {renderPayoutBalance()}
            </div>
            <p style={{ color: 'white', marginTop: '10px' }}>
              <span>{I18n.t('billingUsage.totalBalance')}:</span>{' '}
              {formatCurrency(
                isInvoiceBillingMode
                  ? billingUsage?.cashBalance + gsnCreditBalance || 0
                  : billingUsage?.cashBalance + billingUsage?.creditBalance ||
                      0,
              )}
            </p>
          </div>
        )}
        {/* Auto Recharge Section */}
        <div className={`auto-recharge-section`}>
          {billingUsage?.billingMode !== FLEET_BILLING_MODE.invoice && (
            <div
              className={`rechargeContainer ${
                isAutoRechargeOn
                  ? 'auto-recharge-section-active'
                  : 'auto-recharge-section-inactive'
              }`}
            >
              <IoIosInformationCircleOutline
                className="recharge-icon"
                fontSize={18}
                color="#98A2B3"
              />
              <div className="recharge-content">
                <div className="recharge-status">
                  <span
                    className={`status-indicator ${
                      isAutoRechargeOn ? 'txtActiveColor' : 'txtOffColor'
                    }`}
                  ></span>
                  <span className={`status-text`}>
                    {I18n.t('billingUsage.autoRechargeIs')}{' '}
                    <span
                      className={`${
                        isAutoRechargeOn ? 'txtActiveColor' : 'txtOffColor'
                      }`}
                    >
                      {isAutoRechargeOn
                        ? I18n.t('billingUsage.on')
                        : I18n.t('billingUsage.off')}
                    </span>
                  </span>
                </div>
                <p className="recharge-description">
                  {isAutoRechargeOn
                    ? I18n.t('billingUsage.autoRechargeOn')
                    : I18n.t('billingUsage.autoRechargeOff')}
                </p>
                {permissions.actions && (
                  <Button
                    className="btn-save"
                    onClick={() => setIsShowAutoRechargePopup(true)}
                  >
                    {isAutoRechargeOn
                      ? I18n.t('billingUsage.updateSettingsOn')
                      : I18n.t('billingUsage.updateSettingsOff')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Action Buttons */}
        <div className="action-buttons">
          <Button
            className="btn-action"
            size="sm"
            onClick={() => onTabSelect(TAB_LIST.transactionHistory)}
          >
            <img
              src={viewTransactionIcon}
              alt={I18n.t('billingUsage.viewTransactions')}
            />
            {I18n.t('billingUsage.viewTransactions')}
          </Button>
          {/* <Button className="btn-action" size="sm">
						<img src={viewBankinfoIcon} alt={I18n.t('billingUsage.bankInfo')} />
						{I18n.t('billingUsage.bankInfo')}
					</Button> */}
          <Button
            className="btn-action"
            size="sm"
            onClick={() => setIsShowNetWorkPaymentMethod(true)}
          >
            <img
              src={viewSetupIcon}
              alt={I18n.t('billingUsage.setupPayment')}
            />
            {I18n.t('billingUsage.setupPayment')}
          </Button>
        </div>
        <NetWorkPaymentMethod
          auth={auth}
          commonData={commonData}
          isShow={isShowNetWorkPaymentMethod}
          onClose={() => setIsShowNetWorkPaymentMethod(false)}
          notification={notification}
          loadingActions={loadingActions}
          currencyISO={billingUsage?.currencyISO || 'USD'}
        />
        {
          isShowWithdrawal && (
            <WithdrawalBilling
              isInvoiceBillingMode={isInvoiceBillingMode}
              isShow={isShowWithdrawal}
              onClose={() => setIsShowWithdrawal(false)}
              availableBalance={billingUsage?.cashBalance ?? 0}
              creditBalance={billingUsage?.creditBalance ?? 0}
              currencyISO={billingUsage?.currencyISO || 'AUD'}
              auth={auth}
              notification={notification}
              loadingActions={loadingActions}
              onLoadBillingUsage={onLoadBillingUsage}
            />
          )
        }
        {
          isInvoiceBillingMode && (
            <div>
              <h3>{I18n.t(`${billingUsage?.spendingLimit > 0 ? 'billingUsage.GSNPlatformLimit' : 'billingUsage.GSNPlatform'}`)}</h3>
              <p className="wallet-subtitle">
                {I18n.t( billingUsage?.spendingLimit > 0 ? 'billingUsage.GSNPlatformLimitNote' : 'billingUsage.GSNPlatformNote')}
              </p>
              <div className="balance-cards">
              <Card className="balance-card cash-balance">
                <Card.Body>
                  <div className="card-content">
                    <div className="d-flex align-items-center justify-content-between">
                      <h4>{I18n.t( billingUsage?.spendingLimit > 0 ? 'billingUsage.platformUsage' : 'billingUsage.usageBalance')}</h4>
                    </div>
                    <div className="balance-amount">
                      {formatCurrency(billingUsage?.creditBalance || 0)}
                    </div>
                    {
                      billingUsage?.spendingLimit > 0 && (
                        <p style={{ color: '#98a2b3', marginTop: '15px' }}>
                          {I18n.t('billingUsage.usageBalanceNoteDate')}
                          <b>{` ${I18n.t('billingUsage.usageBalanceNoteDateValue', { date: formatDateBillingNotYear(billingUsage?.currentPeriodEnd) })}`}</b>
                        </p>
                      )
                    }
                  </div>
                </Card.Body>
              </Card>
              </div>
            </div>
          )
        }
      </div>
    </>
  );
};

function mapStateToProps(state) {
  return {
    auth: state.auth,
    commonData: state.commonData,
  };
}

export default connect(mapStateToProps, null)(WalletOverview);
